import { useEffect, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { sanitizeNext } from "@/site/components/auth/auth-utils";
import { fetchEntitlement } from "@/site/components/promote/billing-data";

/**
 * Where embedded Stripe Checkout lands after payment (the server's `return_url` —
 * PRD 06 D3). Entitlement flips via webhook, not the redirect, so this page short-polls
 * `GET /billing/entitlement` and forwards the member to `to` once premium is live.
 * On timeout it degrades honestly: payment went through, activation is just lagging.
 */

type ReturnSearch = { session_id?: string; to?: string };

/**
 * Same-site paths only, via the shared `sanitizeNext` ALLOWLIST — a hand-rolled
 * `startsWith("/")` check passes `/\evil.com`, which browsers normalize to
 * `//evil.com`. This value reaches `window.location.replace()`.
 */
const safeTo = (v: unknown): string =>
  (typeof v === "string" ? sanitizeNext(v) : null) ?? "/payment";

export const Route = createFileRoute("/billing/return")({
  component: BillingReturnPage,
  validateSearch: (search: Record<string, unknown>): ReturnSearch => ({
    session_id: typeof search.session_id === "string" ? search.session_id : undefined,
    to: safeTo(search.to),
  }),
});

const POLL_MS = 1500;
const MAX_POLLS = 20; // ~30s — webhook round-trips are normally a few seconds

function BillingReturnPage() {
  const { to } = Route.useSearch();
  const dest = safeTo(to);
  const [timedOut, setTimedOut] = useState(false);
  const polls = useRef(0);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        const ent = await fetchEntitlement();
        if (cancelled) return;
        if (ent.isEntitled) {
          // Full replace (not router nav): the app re-boots with the premium state
          // everywhere, and Back never returns to this transit page.
          window.location.replace(dest);
          return;
        }
      } catch {
        /* transient — keep polling */
      }
      polls.current += 1;
      if (polls.current >= MAX_POLLS) {
        setTimedOut(true);
        return;
      }
      setTimeout(tick, POLL_MS);
    };
    void tick();
    return () => {
      cancelled = true;
    };
  }, [dest]);

  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 px-6 text-center text-foreground">
      {!timedOut ? (
        <>
          <Loader2 className="size-14 animate-spin text-primary" aria-hidden />
          <h1 className="font-serif text-[1.875rem] font-light">
            Finishing up your membership&hellip;
          </h1>
          <p className="max-w-[440px] text-[1rem] text-grey-dark">
            Your payment went through — we&rsquo;re activating your premium membership now.
          </p>
        </>
      ) : (
        <>
          <h1 className="font-serif text-[1.875rem] font-light">Almost there</h1>
          <p className="max-w-[480px] text-[1rem] text-grey-dark">
            Your payment was received, but activation is taking a little longer than usual. It will
            finish on its own — you can carry on and your membership will be ready shortly.
          </p>
          <Button size="cta" onClick={() => window.location.replace(dest)}>
            Continue
          </Button>
        </>
      )}
    </div>
  );
}
